import { BalanceManager } from "@lib/titan2/dist/api/common/model/data/BalanceManager";
import { DataObject } from "@lib/titan2/dist/api/core/data/DataObject";
import { AppEvent, GameErrorEvent } from "@lib/titan2/dist/game/Constants";
import { AbstractGameController } from "@lib/titan2/dist/game/controller/AbstractGameController";
import { CommonGameEvent } from "@lib/titan2/dist/game/controller/event/CommonGameEvent";
import { ResizeEvent } from "@lib/titan2/dist/game/renderer/Renderer";
import { LoaderEvent } from "@lib/titan2/dist/loader/event/LoaderEvent";
import { Logger } from "@lib/titan2/dist/util/log/Logger";
import { NumberUtil } from "@lib/titan2/dist/util/number/NumberUtil";
import { Service } from "../../api/Service";
import { Slingo } from "../../api/Slingo";
import { ChooseCellParams } from "../../api/choosecell/ChooseCellParams";
import { CollectParams } from "../../api/collect/CollectParams";
import { CurrentGameParams } from "../../api/currentgame/CurrentGameParams";
import { GameState } from "../../api/model/enum/GameState";
import { PurchaseEntryParams } from "../../api/purchase/PurchaseEntryParams";
import { SpinParams } from "../../api/spin/SpinParams";
import { StakeToSpinParams } from "../../api/stakeToSpin/StakeToSpinParams";
import { TweenConstants } from "../view/abstract/constant/TweenConstants";
import { ArcadeWheelEvent } from "../view/abstract/display/ArcadeWheel";
import { TicketController } from "./TicketController";
import { WinController } from "./WinController";
import { SlingoEvent } from "./event/SlingoEvent";
/**
 * SlingoController.ts
 */
export class SlingoController extends AbstractGameController {
    addEventListeners() {
        super.addEventListeners();
        this.registerCore(AppEvent.SETTINGS_LOADED, this.handleSettingsLoaded);
        this.registerCore(AppEvent.ERROR_COMPLETE, this.handleErrorComplete);
        this.registerGame(CommonGameEvent.START_GAME_REQUEST, this.handlePurchaseEntryRequest);
        this.registerGame(CommonGameEvent.SPIN_REQUEST, this.handleSpinRequest);
        this.registerGame(SlingoEvent.CHOOSE_CELL_REQUEST, this.handleChooseCellRequest);
        this.registerGame(SlingoEvent.DAB_COMPLETE, this.handleDabComplete);
        this.registerGame(SlingoEvent.SLINGO, this.handleSlingo);
        this.registerGame(CommonGameEvent.COLLECT_REQUEST, this.handleCollectRequest);
        this.registerGame(CommonGameEvent.NEXT_GAME_REQUEST, this.handleNextGameRequest);
        this.registerGame(SlingoEvent.WIN_DISPLAY_COMPLETE, this.handleWinDisplayComplete);
        this.registerGame(CommonGameEvent.GAME_OVER_CONFIRMED, this.handleGameOverConfirmed);
        this.registerCore(LoaderEvent.LOADER_REMOVE, this.handleLoaderRemove);
        this.registerCore(GameErrorEvent.REALITY_CHECK, this.handleRealityCheckOpen);
        this.registerCore(GameErrorEvent.REALITY_CHECK_CLOSE, this.handleRealityCheckClose);
        this.registerGame(SlingoEvent.BIG_WIN_ANIMATION_COMPLETE, this.handleBigWinAnimationComplete);
        this.registerGame(SlingoEvent.FULL_HOUSE_ANIMATION_COMPLETE, this.handleFullHouseAnimationComplete);
        this.registerGame(CommonGameEvent.GAME_REPLAY, this.handleGameReplay);
        this.registerGame(CommonGameEvent.REPLAY_STOP, this.handleStopReplay);
        this.registerGame(CommonGameEvent.RESPONSIBLE_GAMING_CLOSE, this.handleResponsibleGamingClose);
        this.registerGame(ArcadeWheelEvent.MAX_TOTAL_STAKE_EXCEEDED, this.handleArcadeWheelMaxStakeExceeded);
        this.registerGame(ArcadeWheelEvent.REPLAY_SPIN_COMPLETE, this.updateSpinState);
    }
    removeEventListeners() {
        this.unregisterCore(AppEvent.SETTINGS_LOADED);
        this.unregisterCore(AppEvent.ERROR_COMPLETE);
        this.unregisterGame(CommonGameEvent.START_GAME_REQUEST);
        this.unregisterGame(CommonGameEvent.SPIN_REQUEST);
        this.unregisterGame(SlingoEvent.CHOOSE_CELL_REQUEST);
        this.unregisterGame(SlingoEvent.DAB_COMPLETE);
        this.unregisterGame(SlingoEvent.SLINGO);
        this.unregisterGame(CommonGameEvent.COLLECT_REQUEST);
        this.unregisterGame(CommonGameEvent.NEXT_GAME_REQUEST);
        this.unregisterGame(SlingoEvent.WIN_DISPLAY_COMPLETE);
        this.unregisterGame(CommonGameEvent.GAME_OVER_CONFIRMED);
        this.unregisterCore(LoaderEvent.LOADER_REMOVE);
        this.unregisterCore(GameErrorEvent.REALITY_CHECK);
        this.unregisterCore(GameErrorEvent.REALITY_CHECK_CLOSE);
        this.unregisterGame(SlingoEvent.BIG_WIN_ANIMATION_COMPLETE);
        this.unregisterGame(SlingoEvent.FULL_HOUSE_ANIMATION_COMPLETE);
        this.unregisterGame(CommonGameEvent.FREE_ROUNDS_NEW_GAME_REQUEST);
        this.unregisterGame(CommonGameEvent.GAME_REPLAY);
        this.unregisterGame(CommonGameEvent.REPLAY_STOP);
        this.unregisterGame(CommonGameEvent.RESPONSIBLE_GAMING_CLOSE);
        this.unregisterGame(ArcadeWheelEvent.MAX_TOTAL_STAKE_EXCEEDED);
        this.unregisterGame(ArcadeWheelEvent.REPLAY_SPIN_COMPLETE);
    }
    create() {
        super.create();
    }
    postCreate() {
        super.postCreate();
        this.lastSpinTime = new Date().getTime();
        Logger.addObjectRef(this, "SlingoController");
    }
    createModel() {
        super.createModel();
        this.model = this.baseModel;
    }
    createFactory(factory) {
        super.createFactory(factory);
        this.factory = factory;
    }
    createControllers() {
        this.createTicketController();
        this.createWinController();
    }
    createTicketController() {
        this.ticketController = new TicketController(this.baseFactory);
    }
    createWinController() {
        this.winController = new WinController(this.baseFactory);
    }
    handleResponsibleGamingClose() {
        if (Service.isReplay()) {
            this.handleSpinRequest();
        }
    }
    /* Requests */
    nextGame() {
        if (this.isRequestPermitted()) {
            let parameters = new CurrentGameParams(this.model.getRoomName());
            Slingo.getCurrentGame(parameters, this.handleCurrentGameResult, this);
        }
    }
    handlePurchaseEntryRequest() {
        Logger.verbose("[SlingoController] - handlePurchaseEntryRequest()");
        if (this.isRequestPermitted()) {
            Logger.verbose("[SlingoController] - handlePurchaseEntryRequest() permitted replay");
            let frUUID = "";
            let stake = this.model.getCurrentStake();
            if (this.model.getFreeRoundsData().isFreeRoundsEnabled()) {
                stake = this.model.getFreeRoundsData().getStake();
                frUUID = this.model.getFreeRoundsData().getUUID();
            }
            let currency = this.factory.getWrapperConfig().getOperatorParameters().getCurrency(); // This should be moved to the model
            let parameters = new PurchaseEntryParams(currency, this.model.getTicketId(), stake, this.error.hasAcknowledged(), this.model.getRoomName(), frUUID);
            Logger.verbose("[SlingoController] - makePurchaseEntryRequest()");
            this.model.dispatchKPI("TITAN_STATE", "GAME_ROUND_STARTED", null);
            this.makePurchaseEntryRequest(parameters);
        }
    }
    makePurchaseEntryRequest(parameters) {
        Slingo.purchaseEntry(parameters, this.handlePurchaseEntryResult, this);
    }
    handleSpinRequest() {
        if (Service.isReplay()) {
            if (Service.isReplayStopping()) {
                this.stopReplay();
            }
            else {
                if (Service.isReplaySpinsComplete()) {
                    if (Service.isReplayStakeToSpinsComplete()) {
                        this.dispatchGame(ArcadeWheelEvent.HIDE);
                        this.handleSendCollectRequest();
                    }
                    else {
                        let parameters = new StakeToSpinParams(this.error.hasAcknowledged(), this.model.getRoomName());
                        this.makeStakeToSpinRequest(parameters);
                    }
                }
                else {
                    let parameters = new SpinParams(this.error.hasAcknowledged(), this.model.getRoomName());
                    this.makeSpinRequest(parameters);
                }
            }
        }
        else {
            this.lastSpinTime = new Date().getTime();
            if (this.model.isStakeToSpin()) {
                if (this.isRequestPermitted()) {
                    this.responsibleGaming.resetActions();
                    this.winController.reset();
                    let parameters = new StakeToSpinParams(this.error.hasAcknowledged(), this.model.getRoomName());
                    this.makeStakeToSpinRequest(parameters);
                }
            }
            else if (this.isThresholdValid()) {
                if (this.isRequestPermitted()) {
                    const gameConfig = this.model.getGameConfig();
                    const gameProgress = this.model.getGameProgress();
                    const standardSpins = gameConfig.getNumberOfSpins();
                    const spinsRemaining = gameProgress ? gameProgress.getSpinsRemaining() : gameConfig.getNumberOfSpins();
                    const freeSpinsAwarded = gameProgress ? gameProgress.getFreeSpinsAwarded() : 0;
                    const freeSpinsRemaining = gameProgress ? gameProgress.getFreeSpinsRemaining() : 0;
                    if (freeSpinsRemaining > 0) {
                        const freeSpinIndex = freeSpinsAwarded - freeSpinsRemaining;
                        this.model.dispatchKPI("SLINGO_STATE", "FREE_SPIN_STARTED", `${freeSpinIndex + 1}`); //1-indexed
                    }
                    else if (spinsRemaining <= 0) {
                        this.model.dispatchKPI("SLINGO_STATE", "PURCHASE_SPIN_STARTED", `${this.model.getGameProgress()?.getPurchaseSpinsUsed() + 1}`); //1-indexed
                    }
                    else {
                        const spinIndex = standardSpins - spinsRemaining;
                        this.model.dispatchKPI("SLINGO_STATE", "STANDARD_SPIN_STARTED", `${spinIndex + 1}`); //1-indexed
                    }
                    this.responsibleGaming.resetActions();
                    this.winController.reset();
                    let parameters = new SpinParams(this.error.hasAcknowledged(), this.model.getRoomName());
                    this.makeSpinRequest(parameters);
                }
            }
            else {
                this.model.setResponsibleGamingPriceThresholdDialogOpen(true);
                this.dispatchCore(GameErrorEvent.RESPONSIBLE_GAMING_PRICE_THRESHOLD);
                this.winController.reset();
            }
        }
    }
    makeSpinRequest(parameters) {
        Slingo.spin(parameters, this.handleSpinResult, this);
    }
    makeStakeToSpinRequest(parameters) {
        Slingo.stakeToSpin(parameters, this.handleStakeToSpinResult, this);
    }
    handleSendCollectRequest() {
        this.handleCollectRequest();
    }
    handleChooseCellRequest() {
        if (this.isRequestPermitted()) {
            let selectedCell = this.model.getDabData().getSelectedCell();
            let parameters = new ChooseCellParams(selectedCell.getNumber(), this.error.hasAcknowledged(), this.model.getRoomName());
            this.makeChooseCellRequest(parameters);
        }
    }
    makeChooseCellRequest(parameters) {
        Slingo.chooseCell(parameters, this.handleChooseCellResult, this);
    }
    handleCollectRequest() {
        if (this.isRequestPermitted() && !this.model.hasCollected()) {
            this.winController.reset();
            this.model.setCollected(true);
            this.makeCollectRequest();
        }
    }
    makeCollectRequest() {
        let parameters = new CollectParams(false);
        Slingo.collect(parameters, this.handleCollectResult, this);
    }
    handleGameOverConfirmed() {
        let delay = this.model.hasCollected() ? 0 : this.model.getCompleteInterval();
        this.callAfter(delay, () => {
            if (this.model.getSpinResultResolving()) {
                console.warn("spin result not resolved at function call");
                return;
            }
            this.handleComplete();
        });
    }
    handleRealityCheckOpen() {
        if (!Service.isReplay()) {
            this.model.setAutoSpin(false);
        }
    }
    handleRealityCheckClose() {
        this.model.setAutoSpin(true);
        //GM-701 - retrigger spins if a reality check managed to appear between opening the replay menu and selecting a replay.
        if (Service.isReplay()) {
            this.dispatchGame(CommonGameEvent.SPIN_REQUEST);
        }
    }
    handleBigWinAnimationComplete() {
        this.handleDispatchWinDisplay();
    }
    handleFullHouseAnimationComplete() {
        this.handleDispatchWinDisplay();
    }
    /* Helper Methods */
    resetModel() {
        this.model.reset();
    }
    updateTicket(ticket) {
        this.model.setTicket(ticket);
    }
    updateGameUpdate(update) {
        this.model.setActions(update.getActions());
        this.updateGameInstanceId(update.getGameInstanceId());
        this.updateGameProgress(update.getGameProgress());
        this.updateState(update.getState());
        this.updateSpinResult(update.getSpinResult());
        if (update.getGameProgress() != null) {
            this.updateSpinPrice(update.getGameProgress().getNextSpinPrice());
        }
        else {
            this.updateSpinPrice(0);
        }
        this.updateSymbolWins(update.getSymbolWins());
        this.updatePatternWin(update.getPatternWin());
        this.updateTotalWin();
        if (update.hasBalance()) {
            Logger.verbose("Update Balance in game Controller");
            this.model.setFinalBalance(update.getBalance());
            let balance;
            if (update.hasInitialBalance()) {
                balance = update.getInitialBalance();
            }
            else {
                balance = update.getBalance();
            }
            this.updateBalance(balance);
        }
    }
    updateTotalWin() {
        let progress = this.model.getGameProgress();
        let totalWin = 0;
        if (progress) {
            let symbolWin = progress.getSymbolWin();
            let patternWin = progress.getPatternWin();
            if (this.model.isComplete()) {
                totalWin = symbolWin + patternWin;
            }
            else {
                let matchedPatterns = this.model.getCurrentMatchedPatterns();
                let payoutConfig = this.model.getPayoutConfig();
                let payouts = payoutConfig.getPatternPayouts();
                let stake = this.model.getCurrentStake();
                let currentPatternWin = matchedPatterns > 0 ? payouts[matchedPatterns - 1] * stake : 0;
                totalWin = symbolWin + currentPatternWin;
                this.model.setCurrentPatternWin(currentPatternWin);
            }
        }
        this.model.setTotalWin(totalWin);
        this.dispatchGame(SlingoEvent.WIN_UPDATED);
    }
    updateBalance(value) {
        if (value) {
            this.model.setBalance(value);
            const balanceManager = BalanceManager.getInstance();
            balanceManager.dispatchBalanceUpdated();
            this.dispatchGame(SlingoEvent.BALANCE_UPDATE);
        }
    }
    updateGameProgress(progress) {
        this.model.setGameProgress(progress);
        if (progress && progress.getPurchaseSpinTotalStaked()) {
            this.model.setPurchasedSpinTotal(progress.getPurchaseSpinTotalStaked());
        }
    }
    updateSymbolWins(wins) {
        this.model.setSymbolWins(wins);
    }
    updatePatternWin(win) {
        this.model.setPatternWin(win);
    }
    updateState(state) {
        Logger.debug("SlingoController.updateState: " + state.getType());
        this.model.setState(state);
    }
    updateSpinPrice(price) {
        this.model.setSpinPrice(price);
    }
    updateSpinResult(spinResult) {
        this.model.setSpinResult(spinResult);
    }
    updateGameInstanceId(value) {
        this.model.setGameInstanceId(value);
    }
    updateConfig(config) {
        this.model.setGameConfig(config.getGameConfig());
        this.model.setSymbolConfig(config.getSymbolConfig());
        this.model.setPayoutConfig(config.getPayoutConfig());
        this.model.setInstructionsConfig(config.getInstructionsConfig());
    }
    updateStakeConfig(game) {
        let config = game.getConfig();
        let gameUpdate = game.getGameUpdate();
        let stakeData = config.getStakeConfig();
        if (this.model.isInitialised()) {
            let previousStakeData = this.model.getStake();
            if (gameUpdate.hasLastStake() || gameUpdate.hasFreeGame()) {
                previousStakeData.setIndex(stakeData.getIndex());
            }
        }
        else {
            this.model.setStake(stakeData);
        }
    }
    updateFreeGame(update) {
        this.model.setFreeGame(update.hasFreeGame());
        this.model.setFreeGamesRemaining(update.getFreeGamesRemaining());
    }
    updateCurrentGame(currentGame) {
        if (currentGame.isRecovery()) {
            if (!Service.isReplay() && this.model.isExpressGame() && this.model.getGameProgress().getPurchaseSpinsUsed() === 0) {
                const spinResult = currentGame?.getGameUpdate()?.getSpinResult();
                this.model.setJokerStored(spinResult?.getJokerStored());
                this.model.setSuperJokerStored(spinResult?.getSuperJokerStored());
                this.model.setPreviousJokerStored(spinResult?.getPreviousJokerStored());
                this.model.setPreviousSuperJokerStored(spinResult?.getPreviousSuperJokerStored());
                this.model.setTotalJokerStored(spinResult?.getTotalJokerStored());
                this.model.setTotalSuperJokerStored(spinResult?.getTotalSuperJokerStored());
                this.dispatchGame(SlingoEvent.UPDATE_WILD_COUNTER);
            }
            this.updateGameRecovery(currentGame.getRecovery());
        }
        else {
            this.updateGame();
        }
    }
    updateGameRecovery(recovery) {
        let progress = this.model.getGameProgress();
        this.model.setFirstGame(false);
        this.model.setFreeGame(/*recovery.isFreeGame()*/ false);
        this.model.setRecovery(recovery);
        this.model.setFreeSpinIndex(progress.getFreeSpinsRemaining());
        this.ticketController.updateRecovery();
        this.updateTotalWin();
        if (progress.getFreeSpinsRemaining() > 0 && progress.getSpinsRemaining() === 0) {
            this.handleFreeSpinRecovery();
        }
        else if (progress.getSpinsRemaining() > 0 || (progress.getPurchaseSpinsUsed() === 0 && this.model.isChooseCell())) {
            this.handleStandardSpinRecovery();
        }
        else if (progress.getPurchasableSpinsAvailable() > 0) {
            this.handlePurchaseSpinRecovery();
        }
        this.ticketController.updateDabRecovery();
    }
    updateGame() {
        if (this.model.isPurchaseEntry()) {
            this.dispatchGame(CommonGameEvent.PURCHASE_ENTRY);
        }
        else if (this.model.isComplete()) {
            this.updateTotalWin();
            this.determineCompleteBehaviour();
        }
        else if (!this.model.getSpinResultResolving()) {
            this.updateSpinState();
        }
    }
    determineCompleteBehaviour() {
        const completeReason = this.model.getGameProgress().getCompleteReason();
        const isFullHouse = this.getResultData().isFullHouse();
        const noSpinPrice = this.model.getSpinPriceAmount() == 0;
        const hasCollected = this.model.hasCollected();
        const hasSpinsRemaining = this.model.getGameProgress().getPurchasableSpinsRemaining() > 0;
        if (this.model.getSpinResultResolving()) {
            console.warn("spin result not resolved at function call");
            return;
        }
        if (completeReason?.isMaximumTotalStakeLimit()) {
            this.dispatchGame(CommonGameEvent.MAX_TOTAL_STAKE_EXCEEDED);
        }
        else if (completeReason?.isMaximumStakeLimit()) {
            this.dispatchGame(CommonGameEvent.MAX_PURCHASE_STAKE_EXCEEDED);
        }
        else if (noSpinPrice && !isFullHouse && hasSpinsRemaining) {
            this.dispatchGame(CommonGameEvent.GAME_OVER_CONFIRM);
        }
        else {
            let delay = hasCollected ? 0 : this.model.getCompleteInterval();
            this.callAfter(delay, this.handleComplete);
        }
    }
    updateSpinState() {
        if (this.model.isSpin()) {
            const currentSpinTime = new Date().getTime();
            const timeSinceLastSpin = currentSpinTime - this.lastSpinTime;
            const minSpinDuration = this.model.getGameConfig().getMinimumSpinDuration() * 1000;
            const spinDelay = Math.max(0, minSpinDuration - timeSinceLastSpin);
            this.lastSpinTime = currentSpinTime + spinDelay;
            if (this.model.isStandardSpin()) {
                this.dispatchGame(CommonGameEvent.SPIN);
                if (this.model.isAutoSpin()) {
                    if (Service.isReplay() && !Service.isReplayStopping() && this.model.isStakeToSpin()) {
                        this.dispatchGame(ArcadeWheelEvent.REPLAY_SPIN);
                    }
                    else {
                        this.callAfter(spinDelay, () => {
                            if (this.model.getSpinResultResolving()) {
                                console.warn("spin result not resolved at function call");
                                return;
                            }
                            this.dispatchGame(CommonGameEvent.SPIN_REQUEST);
                        });
                    }
                }
            }
            else if (this.model.isFreeSpin()) {
                this.model.setFreeSpinIndex(0);
                this.dispatchGame(CommonGameEvent.FREE_SPIN);
                if (this.model.isAutoSpin()) {
                    this.callAfter(spinDelay, () => {
                        if (this.model.getSpinResultResolving()) {
                            console.warn("spin result not resolved at function call");
                            return;
                        }
                        this.dispatchGame(CommonGameEvent.SPIN_REQUEST);
                    });
                }
            }
            else if (this.model.isPurchaseSpin()) {
                this.handlePurchaseSpin(spinDelay);
            }
        }
    }
    updateSpinPriceTotal() {
        let previousPrice = this.model.getSpinPriceAmount();
        if (previousPrice > 0) {
            let purchasedSpinTotal = this.model.getPurchasedSpinTotal();
            purchasedSpinTotal = NumberUtil.round(purchasedSpinTotal + previousPrice);
            this.model.setPurchasedSpinTotal(purchasedSpinTotal);
        }
    }
    /* Handlers */
    handleCurrentGameResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            let currentGame = result.getData();
            let gameUpdate = currentGame.getGameUpdate();
            this.dispatchGame(CommonGameEvent.CURRENT_GAME_RESULT);
            this.model.setCurrentGame(currentGame);
            this.resetModel();
            this.updateConfig(currentGame.getConfig());
            this.updateStakeConfig(currentGame);
            this.updateTicket(currentGame.getTicket());
            this.updateGameUpdate(gameUpdate);
            this.updateResponsibleGaming();
            this.updateFreeGame(gameUpdate);
            this.handleInitialise();
            this.updateCurrentGame(currentGame);
        }
        else {
            this.handleErrorResponse(responseCode);
        }
        if (Service.isReplay()) {
            //Automatically continue to purchase entry when running a replay
            Logger.verbose("[SlingoController] - Replay purchase entry");
            this.handlePurchaseEntryRequest();
        }
    }
    handlePurchaseEntryResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            let data = result.getData();
            this.dispatchGame(CommonGameEvent.WAGER_RESPONSE);
            this.updateGameInstanceId(data.getGameInstanceId());
            this.updateState(data.getState());
            this.updateBalance(data.getBalance());
            this.updateClient(data);
            this.dispatchGame(CommonGameEvent.START_GAME);
            this.updateGame();
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    handleSpinResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            this.dispatchGame(CommonGameEvent.WAGER_RESPONSE);
            let data = result.getData();
            let update = data.getGameUpdate();
            this.updateSpinPriceTotal();
            this.updateGameUpdate(update);
            this.updateClient(update);
            this.updateResponsibleGaming();
            this.model.setSpinResultResolving(true);
            if (this.model.isExpressGame()) {
                const result = data.getGameUpdate().getSpinResult();
                this.model.setJokerStored(result.getJokerStored());
                this.model.setSuperJokerStored(result.getSuperJokerStored());
                this.model.setPreviousJokerStored(result.getPreviousJokerStored());
                this.model.setPreviousSuperJokerStored(result.getPreviousSuperJokerStored());
                this.model.setTotalJokerStored(result.getTotalJokerStored());
                this.model.setTotalSuperJokerStored(result.getTotalSuperJokerStored());
            }
            this.dispatchGame(SlingoEvent.SPIN_RESULT);
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    handleStakeToSpinResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            this.dispatchGame(CommonGameEvent.WAGER_RESPONSE);
            let data = result.getData();
            let update = data.getGameUpdate();
            this.updateSpinPriceTotal();
            this.updateGameUpdate(update);
            this.updateClient(update);
            this.updateResponsibleGaming();
            this.model.setSpinResultResolving(true);
            this.dispatchGame(ArcadeWheelEvent.STAKE_TO_SPIN_RESULT);
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    handleChooseCellResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            let data = result.getData();
            let update = data.getGameUpdate();
            this.updateGameUpdate(update);
            this.updateClient(update);
            if (this.model.isExpressGame() && this.model.getGameProgress().getPurchaseSpinsUsed() === 0) {
                const result = data.getGameUpdate().getSpinResult();
                this.model.setJokerStored(result.getJokerStored());
                this.model.setSuperJokerStored(result.getSuperJokerStored());
                this.model.setPreviousJokerStored(result.getPreviousJokerStored());
                this.model.setPreviousSuperJokerStored(result.getPreviousSuperJokerStored());
                this.model.setTotalJokerStored(result.getTotalJokerStored());
                this.model.setTotalSuperJokerStored(result.getTotalSuperJokerStored());
                this.dispatchGame(SlingoEvent.UPDATE_WILD_COUNTER);
            }
            this.dispatchGame(SlingoEvent.CHOOSE_CELL_RESULT);
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    handleCollectResult(result) {
        let responseCode = result.getResponseCode();
        if (this.isSuccess(responseCode)) {
            let data = result.getData();
            let update = data.getGameUpdate();
            this.updateGameUpdate(update);
            this.updateClient(update);
            this.updateGame();
        }
        else {
            this.handleErrorResponse(responseCode);
        }
    }
    updateClient(data) {
        /*            if (data.hasClient()) {
                        let client: IClient = data.getClient();
                        this.model.setClient(client);
                        this.dispatchGame(CommonGameEvent.UPDATE_CLIENT);
                        BalanceManager.getInstance().updateActiveBalanceTypeFromClientData(client);
                    }*/
    }
    handleInitialise() {
        if (this.model.isInitialised()) {
            this.dispatchGame(SlingoEvent.STAKE_INITIALISE);
        }
        else {
            this.model.setInitialised(true);
            this.createControllers();
            this.dispatchCore(LoaderEvent.COMPLETE);
            this.dispatchGame(CommonGameEvent.INITIALISE);
        }
    }
    handleLoaderRemove() {
        this.dispatchCore(ResizeEvent.RESIZE, 1);
    }
    handleStandardSpinRecovery() {
        let config = this.model.getGameConfig();
        let progress = this.model.getGameProgress();
        let spinsRemaining = progress.getSpinsRemaining();
        let previousState = GameState.STANDARD_SPIN;
        if (spinsRemaining == config.getNumberOfSpins()) {
            previousState = GameState.PURCHASE_ENTRY;
        }
        this.model.setPreviousState(previousState);
        this.dispatchGame(CommonGameEvent.STANDARD_SPIN_RECOVERY);
    }
    handleFreeSpinRecovery() {
        let progress = this.model.getGameProgress();
        let previousState = GameState.FREE_SPIN;
        if (progress.isFirstFreeSpin()) {
            previousState = GameState.STANDARD_SPIN;
        }
        this.model.setPreviousState(previousState);
        this.dispatchGame(CommonGameEvent.FREE_SPIN_RECOVERY);
    }
    handlePurchaseSpinRecovery() {
        let progress = this.model.getGameProgress();
        let previousState = GameState.PURCHASE_SPIN;
        if (progress.isFirstFreeSpin()) {
            let freeSpinsAwarded = progress.getFreeSpinsAwarded();
            previousState = freeSpinsAwarded > 0 ? GameState.FREE_SPIN : GameState.STANDARD_SPIN;
        }
        this.model.setPreviousState(previousState);
        this.handlePurchaseSpin(0);
        this.dispatchGame(CommonGameEvent.PURCHASE_SPIN_RECOVERY);
    }
    handlePurchaseSpin(minSpinDelay) {
        let chooseCell = this.model.isChooseCell();
        let valid = this.isLimitsValid();
        if (chooseCell || valid) {
            this.callAfter(Math.max(TweenConstants.AVAILABLE_WIN_DELAY_MS, minSpinDelay), () => {
                if (this.model.getSpinResultResolving()) {
                    console.warn("spin result not resolved at function call");
                    return;
                }
                this.dispatchGame(CommonGameEvent.PURCHASE_SPIN);
                if (this.model.isStakeToSpin() && this.model.hasStakeToSpinAttemptsRemaining()) {
                    this.dispatchGame(ArcadeWheelEvent.SHOW);
                }
            });
        }
        else {
            let reason = this.responsibleGaming.getTrigger();
            if (reason.isExtraSpin()) {
                this.handleLimitReached();
            }
            else {
                this.dispatchCore(GameErrorEvent.RESPONSIBLE_GAMING_LIMIT, 100);
            }
        }
    }
    handleDabComplete() {
        Logger.debug("SlingoController.handleDabComplete");
        this.model.setSpinResultResolving(false);
        this.updateGame();
    }
    handleSlingo() {
        this.updateTotalWin();
    }
    handleNextGameRequest() {
        this.model.dispatchKPI("TITAN_STATE", "GAME_COMPLETE", null);
        this.model.setFirstGame(false);
        this.nextGame();
    }
    handleWinDisplayComplete() {
        if (Service.isReplay()) {
            Service.stopReplay();
        }
        this.updateBalance(this.model.getFinalBalance());
        this.dispatchGame(CommonGameEvent.NEXT_GAME_REQUEST);
    }
    handleComplete() {
        let resultData = this.getResultData();
        if (resultData.isFullHouse() && !resultData.isLowWin()) {
            this.dispatchGame(SlingoEvent.FULL_HOUSE_ANIMATION_PHASE_ONE);
        }
        else if (resultData.isBigWin() && !resultData.isLowWin()) {
            resultData.setMatchedPatterns(this.model.getMatchedPatterns().getValues());
            this.dispatchGame(SlingoEvent.BIG_WIN_ANIMATION_PHASE_ONE);
        }
        else {
            this.handleDispatchWinDisplay();
        }
    }
    handleDispatchWinDisplay() {
        this.dispatchGame(SlingoEvent.WIN_DISPLAY_START);
    }
    handleErrorComplete() {
        Logger.verbose("Error Complete in Slingo Controller");
        if (this.responsibleGaming.isPriceConfirm()) {
            this.model.setResponsibleGamingPriceThresholdDialogOpen(false);
            this.handleSpinRequest();
            this.responsibleGaming.resetActions();
        }
        else if (this.responsibleGaming.isPriceCancel()) {
            this.updateGame();
            this.model.setResponsibleGamingPriceThresholdDialogOpen(false);
            this.responsibleGaming.resetActions();
        }
        else if (this.responsibleGaming.isValid()) {
            this.updateGame();
        }
        else {
            this.handleLimitReached();
        }
    }
    handleLimitReached() {
        this.responsibleGaming.completeGame(this.model.getGameInstanceId());
        this.dispatchGame(CommonGameEvent.RESPONSIBLE_GAMING_SET_TRIGGER);
        this.responsibleGaming.reset();
        this.callAfter(350, this.handleCollectRequest);
    }
    handleArcadeWheelMaxStakeExceeded() {
        this.model.setSpinResultResolving(false);
        this.updateGame();
    }
    getResultData() {
        const payoutConfig = this.model.getPayoutConfig();
        const totalPatterns = payoutConfig.getPatterns().length;
        const progress = this.model.getGameProgress();
        const matchedPatterns = progress.getMatchedPatterns();
        const totalWin = this.model.getTotalWin();
        const stake = this.model.getCurrentStake();
        const patternWin = this.model.getGameProgress().getPatternWin();
        const bigWinThreshold = this.model.getBigWinThreshold();
        const purchasedSpinTotal = this.model.getPurchasedSpinTotal();
        const bigWinTotalStake = this.model.isUsePurchaseSpinTotal() ? stake + purchasedSpinTotal : stake;
        const resultData = this.model.getResultData();
        const currentGame = this.model.getCurrentGame();
        const gameConfig = currentGame.getConfig();
        const gameConfigData = gameConfig.getGameConfig();
        const totalStaked = this.model.getTotalStaked();
        const celebrateStake = stake * gameConfigData.getCelebrateWinThreshold();
        resultData.setSymbolWin(progress.getSymbolWin());
        resultData.setSlingoWin(patternWin);
        resultData.setFullHouse(matchedPatterns == totalPatterns);
        resultData.setNoWin(totalWin <= 0);
        resultData.setLowWin(totalWin < totalStaked);
        resultData.setSmallWin(celebrateStake >= totalWin);
        resultData.setWin2x(stake * 2 <= totalWin);
        resultData.setBigWin(bigWinTotalStake * bigWinThreshold <= totalWin);
        resultData.setWin25x(stake * 25 <= totalWin);
        return resultData;
    }
    handleDialogInactive() {
        const gameProgress = this.model.getGameProgress();
        const spinsRemaining = gameProgress?.getSpinsRemaining() + gameProgress?.getFreeSpinsRemaining();
        if (spinsRemaining && spinsRemaining > 0) {
            this.model.setAutoSpin(true);
            this.updateSpinState();
        }
    }
    /* Responsible Gaming */
    handleSettingsLoaded() {
        this.nextGame();
    }
    updateResponsibleGaming() {
        if (!Service.isReplay()) {
            let progress = this.model.getGameProgress();
            if (progress) {
                let spinsRemaining = progress.getPurchasableSpinsRemaining();
                let spinsAvailable = progress.getPurchasableSpinsAvailable();
                this.responsibleGaming.update(spinsRemaining, spinsAvailable);
            }
        }
    }
    isLimitsValid() {
        let stake = this.model.getCurrentStake();
        let spinPrice = this.model.getSpinPriceAmount();
        let totalStake = this.model.getTotalStaked();
        let totalWin = this.model.getTotalWin();
        return this.responsibleGaming.isLimitsValid(stake, spinPrice, totalStake, totalWin);
    }
    isThresholdValid() {
        let stake = this.model.getCurrentStake();
        let spinPrice = this.model.getSpinPriceAmount();
        let valid = this.responsibleGaming.isThresholdValid(stake, spinPrice);
        let confirm = this.responsibleGaming.isPriceConfirm();
        return confirm || valid;
    }
    handleGameReplay() {
        Logger.verbose("[SlingoController] - handleGameReplay()");
        Logger.verbose("handle In game DGH in GameController");
        this.startReplay();
    }
    handleStopReplay() {
        Service.stopReplay();
    }
    startReplay() {
        if (!Service.isReplay()) {
            if (this.model.getGameReplay()) {
                Logger.verbose("[SlingoController] - getGameReplay() not null");
                let replayData = new DataObject(JSON.parse(this.model.getGameReplay().getJSON()));
                Service.startReplay(replayData);
                this.model.setAutoSpin(true);
                this.dispatchGame(CommonGameEvent.REPLAY_START);
                this.nextGame();
            }
            else {
                Logger.error("[GameController] - failed to get replay data!!!");
            }
        }
        else {
            Logger.error("[SlingoController] - Replay already in progress");
        }
    }
    stopReplay() {
        Service.reset();
        this.dispatchGame(CommonGameEvent.REPLAY_FINISHED);
        this.nextGame();
    }
}
//# sourceMappingURL=SlingoController.js.map